import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Switch, Redirect } from "react-router-dom";

import LoggedInLayout from "../layout";
import BackdropLoading from "../components/BackdropLoading";
import RouteFallback from "../components/RouteFallback";
import AppErrorBoundary from "../components/AppErrorBoundary";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { EmailDraftsProvider } from "../context/EmailDrafts/EmailDraftsContext";
import { ToastProvider, ToastContainer } from "../components/Toast";
// ⚠️ Padrão do projeto: ToastProvider/ToastContainer (components/Toast)
import Route from "./Route";

// 🚀 Páginas críticas carregadas imediatamente
import Login from "../pages/Login/";
import Landing from "../pages/Landing/";
import OAuthCallback from "../pages/OAuthCallback/";

import { menuKeyForPath } from "../helpers/menuPermissions";

// 🔄 Lazy Loading - Páginas carregadas sob demanda
const Dashboard = lazy(() => import("../pages/Dashboard/"));
const TicketResponsiveContainer = lazy(() => import("../pages/TicketResponsiveContainer"));
const Connections = lazy(() => import("../pages/Connections/"));
const SettingsCustom = lazy(() => import("../pages/SettingsCustom/"));
const ExpiredSubscription = lazy(() => import("../pages/ExpiredSubscription/"));
const Users = lazy(() => import("../pages/Users"));
const Contacts = lazy(() => import("../pages/Contacts/"));
const ContactImportPage = lazy(() => import("../pages/Contacts/import"));
const MomentsChat = lazy(() => import("../pages/Moments"));
const StalledTickets = lazy(() => import("../pages/StalledTickets"));
const Chat = lazy(() => import("../pages/Chat"));
const Queues = lazy(() => import("../pages/Queues/"));
const Tags = lazy(() => import("../pages/Tags/"));
const MessagesAPI = lazy(() => import("../pages/MessagesAPI/"));
const Helps = lazy(() => import("../pages/Helps/"));
const ContactLists = lazy(() => import("../pages/ContactLists/"));
const ContactListItems = lazy(() => import("../pages/ContactListItems/"));
const Companies = lazy(() => import("../pages/Companies/"));
const QuickMessages = lazy(() => import("../pages/QuickMessages/"));
const Schedules = lazy(() => import("../pages/Schedules"));
const Campaigns = lazy(() => import("../pages/Campaigns"));
const CampaignReport = lazy(() => import("../pages/CampaignReport"));
const CampaignBlacklist = lazy(() => import("../pages/CampaignBlacklist"));
const CampaignTemplates = lazy(() => import("../pages/CampaignTemplates"));
const Annoucements = lazy(() => import("../pages/Annoucements"));
// const Prompts = lazy(() => import("../pages/Prompts")); // Removido: Usar AIConfigurations
const AIConfigurations = lazy(() => import("../pages/AIConfigurations"));
const AITools = lazy(() => import("../pages/AITools")); // 🔧 AI Tools Framework
const AIPlayground = lazy(() => import("../pages/AIPlayground"));
const UnbrowseWebSessions = lazy(() => import("../components/UnbrowseWebSessions")); // 🔍 Unbrowse Web Sessions
const SmartAIDashboard = lazy(() => import("../pages/SmartAIDashboard"));
const AllConnections = lazy(() => import("../pages/AllConnections/"));
const Reports = lazy(() => import("../pages/Reports"));
const ReportsAdvanced = lazy(() => import("../pages/ReportsAdvanced"));
const TicketClassification = lazy(() => import("../pages/TicketClassification"));
const TicketTabsSettings = lazy(() => import("../pages/TicketTabsSettings"));
const QueueIntegration = lazy(() => import("../pages/QueueIntegration"));
const GoogleSpeechIntegration = lazy(() => import("../pages/GoogleSpeechIntegration"));
const Files = lazy(() => import("../pages/Files/"));
const ToDoList = lazy(() => import("../pages/ToDoList/"));
const Tasks = lazy(() => import("../pages/Tasks/"));
const Kanban = lazy(() => import("../pages/Kanban"));
const TagsKanban = lazy(() => import("../pages/TagsKanban"));
const Flows = lazy(() => import("../pages/Flows"));
const FlowsList = lazy(() => import("../pages/FlowsList"));
const WhatsappFlows = lazy(() => import("../pages/WhatsappFlows"));
const FlowBuilderPage = lazy(() => import("../pages/FlowBuilderPage"));
const FlowAnalytics = lazy(() => import("../pages/FlowAnalytics"));
const ContactWallets = lazy(() => import("../pages/ContactWallets"));
const ProfilePermissions = lazy(() => import("../pages/ProfilePermissions"));
const StorageManagement = lazy(() => import("../pages/StorageManagement"));
const BackupRestore = lazy(() => import("../pages/BackupRestore"));
const MobileAppQRCode = lazy(() => import("../pages/MobileAppQRCode"));
const IFoodOrders = lazy(() => import("../pages/IFoodOrders"));
const IFoodCatalog = lazy(() => import("../pages/IFoodCatalog"));
const IFoodReviews = lazy(() => import("../pages/IFoodReviews"));
const IFoodFinancial = lazy(() => import("../pages/IFoodFinancial"));
const IFoodPromotions = lazy(() => import("../pages/IFoodPromotions"));
const AdminFinanceiro = lazy(() => import("../pages/AdminFinanceiro"));
const Financeiro = lazy(() => import("../pages/Financeiro"));
const Signup = lazy(() => import("../pages/Signup"));
const AuditLogs = lazy(() => import("../pages/AuditLogs"));
const MobileAppConfig = lazy(() => import("../pages/MobileAppConfig"));
const StripePayments = lazy(() => import("../pages/StripePayments"));
const Pricing = lazy(() => import("../pages/Pricing"));
const EmailConnections = lazy(() => import("../pages/EmailConnections"));
const EmailTemplates = lazy(() => import("../pages/EmailTemplates"));
const WhatsAppGroups = lazy(() => import("../pages/WhatsAppGroups"));
const WhatsAppCommunities = lazy(() => import("../pages/WhatsAppCommunities"));
const WhatsAppChannels = lazy(() => import("../pages/WhatsAppChannels"));
const LeadDashboard = lazy(() => import("../pages/LeadDashboard"));
const Automations = lazy(() => import("../pages/Automations"));
const JourneyDashboard = lazy(() => import("../pages/JourneyDashboard"));
const Callbacks = lazy(() => import("../pages/Callbacks"));
const VoiceApiSettings = lazy(() => import("../pages/VoiceApiSettings"));
const ApiV1Settings = lazy(() => import("../pages/ApiV1Settings"));
const HelpCenter = lazy(() => import("../pages/HelpCenter"));

const Routes = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return null;
  }

  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <TicketsContextProvider>
            <Switch>
              <Route exact path="/landing" component={Landing} />
              <Route exact path="/login" component={Login} />
              <Route exact path="/expired-subscription">
                <Suspense fallback={<RouteFallback />}>
                  <ExpiredSubscription />
                </Suspense>
              </Route>
              <Route exact path="/signup">
                <Suspense fallback={<RouteFallback />}>
                  <Signup />
                </Suspense>
              </Route>
              <Route exact path="/pricing">
                <Suspense fallback={<RouteFallback />}>
                  <Pricing />
                </Suspense>
              </Route>
              <Route exact path="/planos">
                <Suspense fallback={<RouteFallback />}>
                  <Pricing />
                </Suspense>
              </Route>
              {/* 🔗 OAuth Callback - Rota pública para Embedded Signup */}
              <Route exact path="/oauth-callback" component={OAuthCallback} />
              <AuthContext.Consumer>
                {({ isAuth, subscriptionExpired, loading: authLoading }) => {
                  const pathname = window.location.pathname;
                  const isPublicRoute =
                    pathname === "/login" ||
                    pathname === "/signup" ||
                    pathname === "/landing" ||
                    pathname === "/pricing" ||
                    pathname === "/planos" ||
                    pathname === "/expired-subscription" ||
                    pathname === "/oauth-callback";

                  if (authLoading) {
                    return <BackdropLoading />;
                  }

                  // Assinatura vencida: bloquear app logado e mandar para renovação
                  if (subscriptionExpired) {
                    return pathname === "/expired-subscription" ? null : (
                      <Redirect to="/expired-subscription" />
                    );
                  }

                  // Em rotas públicas, não forçar Redirect para evitar loops
                  if (!isAuth && isPublicRoute) {
                    return null;
                  }

                  return isAuth ? (
                    <WhatsAppsProvider>
                      <EmailDraftsProvider>
                      <LoggedInLayout>
                        <Suspense fallback={<RouteFallback />}>
                          <Route
                            exact
                            path="/financeiro"
                            component={Financeiro}
                            isPrivate
                            requiredPermission={menuKeyForPath("/financeiro")}
                          />

                          <Route
                            exact
                            path="/companies"
                            component={Companies}
                            isPrivate
                            requiredPermission={menuKeyForPath("/companies")}
                          />
                          <Route
                            exact
                            path="/admin-financeiro"
                            component={AdminFinanceiro}
                            isPrivate
                            requiredPermission={menuKeyForPath("/admin-financeiro")}
                          />
                          <Route
                            exact
                            path="/stripe-payments"
                            component={StripePayments}
                            isPrivate
                            requiredPermission={menuKeyForPath("/stripe-payments")}
                          />
                          <Route
                            exact
                            path="/audit-logs"
                            component={AuditLogs}
                            isPrivate
                            requiredPermission={menuKeyForPath("/audit-logs")}
                          />
                          {/* App Mobile - QR Code de configuração de servidor.
                              Acesso admin/supervisor/super (gate na própria
                              página + item de menu no MainListItems). */}
                          <Route
                            exact
                            path="/mobile-app-config"
                            component={MobileAppConfig}
                            isPrivate
                            requiredPermission={menuKeyForPath("/mobile-app-config")}
                          />
                          {/* ✅ Rota padrão: Atendimento (Tickets) */}
                          <Route exact path="/" component={TicketResponsiveContainer} isPrivate requiredPermission={menuKeyForPath("/")} />

                          {/* ✅ Dashboard continua disponível */}
                          <Route exact path="/dashboard" component={Dashboard} isPrivate requiredPermission={menuKeyForPath("/dashboard")} />
                        
                        <Route
                          exact
                          path="/flows"
                          component={FlowsList}
                          isPrivate
                          requiredPermission={menuKeyForPath("/flows")}
                        />
                        <Route
                          exact
                          path="/flows/:flowId"
                          component={Flows}
                          isPrivate
                          requiredPermission={menuKeyForPath("/flows/:flowId")}
                        />
                        
                        <Route
                          exact
                          path="/tickets/:ticketId?"
                          component={TicketResponsiveContainer}
                          isPrivate
                          requiredPermission={menuKeyForPath("/tickets/:ticketId?")}
                        />
                        <Route
                          exact
                          path="/connections"
                          component={Connections}
                          isPrivate
                          requiredPermission={menuKeyForPath("/connections")}
                        />
                        <Route
                          exact
                          path="/ifood/orders"
                          component={IFoodOrders}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ifood/orders")}
                        />
                        <Route
                          exact
                          path="/ifood/catalog"
                          component={IFoodCatalog}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ifood/catalog")}
                        />
                        <Route
                          exact
                          path="/ifood/reviews"
                          component={IFoodReviews}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ifood/reviews")}
                        />
                        <Route
                          exact
                          path="/ifood/financial"
                          component={IFoodFinancial}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ifood/financial")}
                        />
                        <Route
                          exact
                          path="/ifood/promotions"
                          component={IFoodPromotions}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ifood/promotions")}
                        />
                        <Route
                          exact
                          path="/quick-messages"
                          component={QuickMessages}
                          isPrivate
                          requiredPermission={menuKeyForPath("/quick-messages")}
                        />
                        <Route
                          exact
                          path="/todolist"
                          component={ToDoList}
                          isPrivate
                          requiredPermission={menuKeyForPath("/todolist")}
                        />
                        <Route
                          exact
                          path="/tasks"
                          component={Tasks}
                          isPrivate
                          requiredPermission={menuKeyForPath("/tasks")}
                        />
                        <Route
                          exact
                          path="/leads"
                          component={LeadDashboard}
                          isPrivate
                          requiredPermission={menuKeyForPath("/leads")}
                        />
                        <Route
                          exact
                          path="/automations"
                          component={Automations}
                          isPrivate
                          requiredPermission={menuKeyForPath("/automations")}
                        />
                        <Route
                          exact
                          path="/journey"
                          component={JourneyDashboard}
                          isPrivate
                          requiredPermission={menuKeyForPath("/journey")}
                        />
                        <Route
                          exact
                          path="/callbacks"
                          component={Callbacks}
                          isPrivate
                          requiredPermission={menuKeyForPath("/callbacks")}
                        />
                        <Route
                          exact
                          path="/schedules"
                          component={Schedules}
                          isPrivate
                          requiredPermission={menuKeyForPath("/schedules")}
                        />
                        <Route exact path="/tags" component={Tags} isPrivate requiredPermission={menuKeyForPath("/tags")} />
                        <Route
                          exact
                          path="/contacts"
                          component={Contacts}
                          isPrivate
                          requiredPermission={menuKeyForPath("/contacts")}
                        />
                        <Route
                          exact
                          path="/contacts/import"
                          component={ContactImportPage}
                          isPrivate
                          requiredPermission={menuKeyForPath("/contacts/import")}
                        />
                        <Route
                          exact
                          path="/helps"
                          component={Helps}
                          isPrivate
                          requiredPermission={menuKeyForPath("/helps")}
                        />
                        <Route
                          path="/help-center/:categoryId?/:articleId?"
                          component={HelpCenter}
                          isPrivate
                          requiredPermission={menuKeyForPath("/help-center/:categoryId?/:articleId?")}
                        />
                        <Route
                          exact
                          path="/users"
                          component={Users}
                          isPrivate
                          requiredPermission={menuKeyForPath("/users")}
                        />
                        <Route
                          exact
                          path="/messages-api"
                          component={MessagesAPI}
                          isPrivate
                          requiredPermission={menuKeyForPath("/messages-api")}
                        />
                        <Route
                          exact
                          path="/settings"
                          component={SettingsCustom}
                          isPrivate
                          requiredPermission={menuKeyForPath("/settings")}
                        />
                        <Route
                          exact
                          path="/queues"
                          component={Queues}
                          isPrivate
                          requiredPermission={menuKeyForPath("/queues")}
                        />
                        <Route
                          exact
                          path="/whatsapp-flows"
                          component={WhatsappFlows}
                          isPrivate
                          requiredPermission={menuKeyForPath("/whatsapp-flows")}
                        />
                        <Route
                          exact
                          path="/whatsapp-flows/:id/edit"
                          component={FlowBuilderPage}
                          isPrivate
                          requiredPermission={menuKeyForPath("/whatsapp-flows/:id/edit")}
                        />
                        <Route
                          exact
                          path="/whatsapp-flows/:id/analytics"
                          component={FlowAnalytics}
                          isPrivate
                          requiredPermission={menuKeyForPath("/whatsapp-flows/:id/analytics")}
                        />
                        <Route
                          exact
                          path="/contact-wallets"
                          component={ContactWallets}
                          isPrivate
                          requiredPermission={menuKeyForPath("/contact-wallets")}
                        />
                        <Route
                          exact
                          path="/profile-permissions"
                          component={ProfilePermissions}
                          isPrivate
                          requiredPermission={menuKeyForPath("/profile-permissions")}
                        />
                        <Route
                          exact
                          path="/storage-management"
                          component={StorageManagement}
                          isPrivate
                          requiredPermission={menuKeyForPath("/storage-management")}
                        />
                        <Route
                          exact
                          path="/backup-restore"
                          component={BackupRestore}
                          isPrivate
                          requiredPermission={menuKeyForPath("/backup-restore")}
                        />
                        <Route
                          exact
                          path="/mobile-app-qr"
                          component={MobileAppQRCode}
                          isPrivate
                          requiredPermission={menuKeyForPath("/mobile-app-qr")}
                        />
                        <Route
                          exact
                          path="/reports"
                          component={Reports}
                          isPrivate
                          requiredPermission={menuKeyForPath("/reports")}
                        />
                        <Route
                          exact
                          path="/reports-advanced"
                          component={ReportsAdvanced}
                          isPrivate
                          requiredPermission={menuKeyForPath("/reports-advanced")}
                        />
                        <Route
                          exact
                          path="/ticket-classification"
                          component={TicketClassification}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ticket-classification")}
                        />
                        <Route
                          exact
                          path="/ticket-tabs-settings"
                          component={TicketTabsSettings}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ticket-tabs-settings")}
                        />
                        <Route
                          exact
                          path="/queue-integration"
                          component={QueueIntegration}
                          isPrivate
                          requiredPermission={menuKeyForPath("/queue-integration")}
                        />
                        <Route
                          exact
                          path="/integrations/google-speech"
                          component={GoogleSpeechIntegration}
                          isPrivate
                          requiredPermission={menuKeyForPath("/integrations/google-speech")}
                        />
                        <Route
                          exact
                          path="/integrations/voice-api"
                          component={VoiceApiSettings}
                          isPrivate
                          requiredPermission={menuKeyForPath("/integrations/voice-api")}
                        />
                        <Route
                          exact
                          path="/integrations/public-api"
                          component={ApiV1Settings}
                          isPrivate
                          requiredPermission={menuKeyForPath("/integrations/public-api")}
                        />
                        <Route
                          exact
                          path="/announcements"
                          component={Annoucements}
                          isPrivate
                          requiredPermission={menuKeyForPath("/announcements")}
                        />
                        <Route
                          exact
                          path="/chats/:id?"
                          component={Chat}
                          isPrivate
                          requiredPermission={menuKeyForPath("/chats/:id?")}
                        />
                        <Route
                          exact
                          path="/files"
                          component={Files}
                          isPrivate
                          requiredPermission={menuKeyForPath("/files")}
                        />
                        <Route
                          exact
                          path="/moments"
                          component={MomentsChat}
                          isPrivate
                          requiredPermission={menuKeyForPath("/moments")}
                        />
                        <Route
                          exact
                          path="/stalled-tickets"
                          component={StalledTickets}
                          isPrivate
                          requiredPermission={menuKeyForPath("/stalled-tickets")}
                        />
                        <Route
                          exact
                          path="/Kanban"
                          component={Kanban}
                          isPrivate
                          requiredPermission={menuKeyForPath("/Kanban")}
                        />
                        <Route
                          exact
                          path="/TagsKanban"
                          component={TagsKanban}
                          isPrivate
                          requiredPermission={menuKeyForPath("/TagsKanban")}
                        />
                        {/* Redirect antigo /prompts para nova página */}
                        <Route
                          exact
                          path="/prompts"
                          render={() => <Redirect to="/ai-configurations" />}
                        />
                        <Route
                          exact
                          path="/ai-configurations"
                          component={AIConfigurations}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ai-configurations")}
                        />
                        <Route
                          exact
                          path="/ai-tools"
                          component={AITools}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ai-tools")}
                        />
                        <Route
                          exact
                          path="/ai-tools/web-sessions"
                          component={UnbrowseWebSessions}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ai-tools/web-sessions")}
                        />
                        <Route
                          exact
                          path="/ai-playground"
                          component={AIPlayground}
                          isPrivate
                          requiredPermission={menuKeyForPath("/ai-playground")}
                        />
                        <Route
                          exact
                          path="/smart-ai-dashboard"
                          component={SmartAIDashboard}
                          isPrivate
                          requiredPermission={menuKeyForPath("/smart-ai-dashboard")}
                        />
                        <Route
                          exact
                          path="/email-connections"
                          component={EmailConnections}
                          isPrivate
                          requiredPermission={menuKeyForPath("/email-connections")}
                        />
                        <Route
                          exact
                          path="/email-templates"
                          component={EmailTemplates}
                          isPrivate
                          requiredPermission={menuKeyForPath("/email-templates")}
                        />
                        <Route
                          exact
                          path="/whatsapp-groups"
                          component={WhatsAppGroups}
                          isPrivate
                          requiredPermission={menuKeyForPath("/whatsapp-groups")}
                        />
                        <Route
                          exact
                          path="/whatsapp-communities"
                          component={WhatsAppCommunities}
                          isPrivate
                          requiredPermission={menuKeyForPath("/whatsapp-communities")}
                        />
                        <Route
                          exact
                          path="/whatsapp-channels"
                          component={WhatsAppChannels}
                          isPrivate
                          requiredPermission={menuKeyForPath("/whatsapp-channels")}
                        />
                        <Route
                          exact
                          path="/allConnections"
                          component={AllConnections}
                          isPrivate
                          requiredPermission={menuKeyForPath("/allConnections")}
                        />
                        {showCampaigns && (
                          <>
                            <Route
                              exact
                              path="/contact-lists"
                              component={ContactLists}
                              isPrivate
                              requiredPermission={menuKeyForPath("/contact-lists")}
                            />
                            <Route
                              exact
                              path="/contact-lists/:contactListId/contacts"
                              component={ContactListItems}
                              isPrivate
                              requiredPermission={menuKeyForPath("/contact-lists/:contactListId/contacts")}
                            />
                            <Route
                              exact
                              path="/campaigns"
                              component={Campaigns}
                              isPrivate
                              requiredPermission={menuKeyForPath("/campaigns")}
                            />
                            <Route
                              exact
                              path="/campaign/:campaignId/report"
                              component={CampaignReport}
                              isPrivate
                              requiredPermission={menuKeyForPath("/campaign/:campaignId/report")}
                            />
                            <Route
                              exact
                              path="/campaign-blacklist"
                              component={CampaignBlacklist}
                              isPrivate
                              requiredPermission={menuKeyForPath("/campaign-blacklist")}
                            />
                            <Route
                              exact
                              path="/campaign-templates"
                              component={CampaignTemplates}
                              isPrivate
                              requiredPermission={menuKeyForPath("/campaign-templates")}
                            />
                          </>
                        )}
                        </Suspense>
                      </LoggedInLayout>
                      </EmailDraftsProvider>
                    </WhatsAppsProvider>
                  ) : (
                    <Redirect to="/login" />
                  );
                }}
              </AuthContext.Consumer>
            </Switch>
            <ToastContainer />
          </TicketsContextProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
};

export default Routes;
